/* eslint-disable @typescript-eslint/no-explicit-any */

import Wrapper from '@/common/wrapper/Wrapper';
import { useRouter } from '@/hooks/hooks';
import { downloadFileByBlob } from '@/pages/common/importExcel/const/const';
import { instance } from '@/service';
import {addLogoPt, getSingleCourseAnalysis} from '@/service/exam/analysis';
import { courseRecord, schoolAreaRecord } from '@/utils/const';
import { Button, Radio } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import style from './Analysis.module.less';
import { AnalysisContext } from './AnalysisPortal';
import {ColumnConfig, courseModuleTypeRecord, getSingleColumnSetting, singleCourseColumn} from './const';
import { exportFile } from 'table-xlsx';
import { filterNull } from '@/utils';
import layer from '@/utils/layer';
import ColumnSetting, { getCachedColumnSetting } from '@pages/front/pages/examCenter/pages/analysis/components/ColumnSetting';
import {cloneDeep} from 'lodash';
import { formatAnalysisTableRows, formatAnalysisTableValue } from './format';

const SCORE_SEGMENT_COLUMN_SETTING_STORAGE_KEY = 'examCenter:analysis:singleCourse:columnSetting';
const SCORE_SEGMENT_COLUMN_SETTING_STORAGE_VERSION = 1;
const SCORE_GROUP_SETTING_KEYS = ['scoreCount', 'scoreRate', 'scoreRank'];

const hasOwn = (target: Record<string, boolean>, key: string) =>
  Object.prototype.hasOwnProperty.call(target, key);

const applyCachedColumnSetting = (source: ColumnConfig<SingleCourseAnalysisStat>[]) => {
  const setting = getCachedColumnSetting(
    SCORE_SEGMENT_COLUMN_SETTING_STORAGE_KEY,
    getSingleColumnSetting(),
    SCORE_SEGMENT_COLUMN_SETTING_STORAGE_VERSION
  );
  const activeMap = setting.reduce((a, c) => {
    return {
      ...a,
      [c.dataIndex]: c.active
    };
  }, {} as Record<string, boolean>);

  return source.map(i => {
    const dataIndex = String(i.dataIndex || '');
    const selfMatched = hasOwn(activeMap, dataIndex);
    const column = {
      ...i,
      active: selfMatched ? activeMap[dataIndex] : i.active
    };
    let childrenChanged = false;

    if (i.children) {
      column.children = i.children.map(j => {
        const childDataIndex = String(j.dataIndex || '');
        const settingKey = Object.keys(activeMap).find(key => {
          if (key === childDataIndex) return true;
          return SCORE_GROUP_SETTING_KEYS.includes(key) && childDataIndex.includes(key);
        });

        if (!settingKey) return j;
        childrenChanged = true;
        return {
          ...j,
          active: activeMap[settingKey]
        };
      });

      if (!selfMatched && childrenChanged) {
        column.active = column.children.some(o => o.active);
      }
    }

    return column;
  });
};

const SingleCourse = () => {

  const router = useRouter();

  const { examid, name } = router.getSearchParams();
  const [split, setSplit] = useState(5);
  const [columns, setColumns] = useState(singleCourseColumn);
  const [list, setList] = useState<Record<string, any>[]>([]);
  const [mouseAdr, setMouseAdr] = useState({x: -1, y: ''});

  const { courseDcode, canExportStudentScore } = useOutletContext<AnalysisContext>();

  const load = async (s: number) => {
    try {
      layer.loading();
      const { data } = await getSingleCourseAnalysis({
        examId: examid,
        scoreGroupStep: s,
        coursetypeDcode: courseDcode
      });
      let newColumn;
      if (Object.keys(data).length > 0) {
        if (data.scoreModuleTypes.length > 0) {
          const children: typeof columns = data.scoreModuleTypes.map(i => ({
            title: courseModuleTypeRecord[i],
            dataIndex: i,
            active: true,
            isAvg: true
          }));
          children.push({
            title: `${courseRecord[courseDcode].slice(0, 1)}总`,
            dataIndex: `${courseDcode}FullScore`,
            isAvg: true,
            render: (_, p) => {
              return p.avgScore;
            },
            active: true
          });
          newColumn = [
            ...singleCourseColumn.map(i => {
              if (i.dataIndex === 'avgScore') {
                return {
                  ...i,
                  active: true,
                  width: 180,
                  children
                };
              } else {
                return i;
              }
            }),
            ...data.scoreGroups.map(i => ({
              title: i.name,
              name: i.name,
              dataIndex: i.name + 'scoreCount',
              active: true,
              children: [
                { title: '人数', active: true, fathername: i.name, dataIndex: i.name + 'scoreCount' },
                { title: '百分比', active: false, fathername: i.name, dataIndex: i.name + 'scoreRate' },
                { title: '排名', active: false, fathername: i.name, dataIndex: i.name + 'scoreRank' }
              ]
            }))
          ];
        } else {
          newColumn = [
            ...singleCourseColumn,
            ...data.scoreGroups.map(i => ({
              title: i.name,
              name: i.name,
              dataIndex: i.name + 'scoreCount',
              active: true,
              children: [
                { title: '人数', active: true, fathername: i.name, dataIndex: i.name + 'scoreCount' },
                { title: '百分比', active: false, fathername: i.name, dataIndex: i.name + 'scoreRate' },
                { title: '排名', active: false, fathername: i.name, dataIndex: i.name + 'scoreRank' }
              ]
            }))
          ];
        }
        const newList = [
          ...data.schoolStatList.map(i => {
            return {
              ...i,
              ...data.scoreModuleTypes.reduce((a, key) => { return { ...a, [key]: i.moduleAvgScore[key] || 0 }; }, {}),
              [`${courseDcode}FullScore`]: i.avgScore,
              ...data.scoreGroups.reduce((a, c) => {
                return { ...a,
                  [c.name + 'scoreCount']: i.scoreGroupCountInfo ? i.scoreGroupCountInfo[c.name]?.stuCount || '0' : '0',
                  [c.name + 'scoreRate']: i.scoreGroupCountInfo ? i.scoreGroupCountInfo[c.name]?.rate || '0%' : '0%',
                  [c.name + 'scoreRank']: i.scoreGroupCountInfo ? i.scoreGroupCountInfo[c.name]?.rateRank || '0' : '0'
                };
              }, {} as Record<string, string>)
            };
          }),
          ...data.areaStatList.map(i => {
            return {
              ...i,
              schoolShortname: schoolAreaRecord[i.areaCode],
              ...data.scoreModuleTypes.reduce((a, key) => { return { ...a, [key]: i.moduleAvgScore[key] || 0 }; }, {}),
              [`${courseDcode}FullScore`]: i.avgScore,
              ...data.scoreGroups.reduce((a, c) => {
                return { ...a,
                  [c.name + 'scoreCount']: i.scoreGroupCountInfo ? i.scoreGroupCountInfo[c.name]?.stuCount || '0' : '0',
                  [c.name + 'scoreRate']: i.scoreGroupCountInfo ? i.scoreGroupCountInfo[c.name]?.rate || '0%' : '0%',
                  [c.name + 'scoreRank']: i.scoreGroupCountInfo ? i.scoreGroupCountInfo[c.name]?.rateRank || '0' : '0'
                };
              }, {} as Record<string, string>)
            };
          })
        ];
        if (data.amountStat) newList.push({
          ...data.amountStat,
          ...data.amountStat.moduleAvgScore,
          [`${courseDcode}FullScore`]: data.amountStat.avgScore,
          ...data.scoreGroups.reduce((a, c) => {
            return { ...a,
              [c.name + 'scoreCount']: data.amountStat.scoreGroupCountInfo ? data.amountStat.scoreGroupCountInfo[c.name]?.stuCount || '0' : '',
              [c.name + 'scoreRate']: data.amountStat.scoreGroupCountInfo ? data.amountStat.scoreGroupCountInfo[c.name]?.rate || '0%' : '',
              [c.name + 'scoreRank']: ''
            };
          }, {} as Record<string, string>)
        });
        setList(newList);
      } else {
        newColumn = [
          ...singleCourseColumn
        ];
        setList([]);
      }
      setColumns(applyCachedColumnSetting(newColumn));
    } finally {
      layer.closeLoading();
    }
  };

  const onColumnChange = (checked: boolean, dataIndex: string) => {
    const newColumns= cloneDeep(columns);
    newColumns.forEach(i => {
      if (i.dataIndex === dataIndex) {
        i.active = checked;
      } else if (i.children) {
        i.children.forEach(j => {
          if (j.dataIndex === dataIndex) {
            j.active = checked;
            return;
          }
          if (['scoreCount', 'scoreRate', 'scoreRank'].indexOf(dataIndex) !== -1) {
            if ((j.dataIndex as string).includes(dataIndex)) {
              j.active = checked;
            }
          }
        });
        i.active = i.children.some(o => o.active);
      }
    });
    setColumns(newColumns);
  };

  const columnReset = (checked: boolean, dataIndex: string[]) => {
    const newColumns = cloneDeep(columns);
    // 特殊处理这两个字段
    const containScoreRate = dataIndex.includes('scoreRate');
    const containScoreRank = dataIndex.includes('scoreRank');
    dataIndex.forEach(d => {
      newColumns.forEach(i => {
        if (i.dataIndex === d) {
          i.active = checked;
        } else if (i.children) {
          i.children.forEach(j => {
            if (j.dataIndex === d) {
              j.active = checked;
              return;
            }
            if (['scoreCount', 'scoreRate', 'scoreRank'].indexOf(d) !== -1) {
              if ((j.dataIndex as string).includes(d)) {
                j.active = checked;
              }
            }
            // 特殊处理
            if (
              (j.dataIndex as string).includes('scoreRate') && !containScoreRate ||
              (j.dataIndex as string).includes('scoreRank') && !containScoreRank
            ) {
              j.active = false;
            }
          });
          i.active = i.children.some(o => o.active);
        }
      });
    });
    setColumns(newColumns);
  };

  const secondRow = useMemo(() => {
    return columns.reduce((a, c) => {
      if (c.children) {
        if (c.children.filter(o => o.active && o.title !== '人数').length === 0) {
          return a;
        } else {
          return [...a, ...c.children];
        }
      }
      else {
        return a;
      }
    }, [] as ColumnConfig<SingleCourseAnalysisStat>[]);
  }, [columns]);

  const bodyColumns = useMemo(() => {
    return columns.reduce((a, c) => {
      if (c.children) {
        return [...a, ...c.children.filter(i => i.active)];
      } else if (c.active) {
        return [...a, c];
      } else {
        return a;
      }
    }, [] as ColumnConfig<SingleCourseAnalysisStat>[]);
  }, [columns, list]);

  const splitChange = (s: number) => {
    setSplit(s);
    load(s);
  };

  useEffect(() => {
    courseDcode && load(split);
  }, [courseDcode]);


  const [exportStudentScoreLoading, setExportStudentScoreLoading] = useState(false);
  const downloadStudentScoreExcel = () => {
    setExportStudentScoreLoading(true);
    instance.get('/teacher/exam/statistic/export/studentscore/single', {
      params: {
        examId: examid,
        coursetypeDcode: courseDcode
      },
      responseType: 'blob'
    }).then(({ data }) => {
      const file = new FileReader();
      file.readAsText(data, 'utf-8');
      file.onload = () => {
        try {
          const res = JSON.parse(file.result as string);
          if (res.code && res.code === 'FAILURE') {
            layer.error(res.msg);
          }
        } catch {
          downloadFileByBlob(data, decodeURI(name) + '-' + courseRecord[courseDcode] + '-学生成绩表.xlsx');
        }
      };
    }).finally(() => {
      setExportStudentScoreLoading(false);
    });
  };

  const exportSingleAnalysisExcel = () => {
    const c = columns.filter(i => i.active).reduce((a, curr) => {
      if (!curr.children || curr.children.length === 0) {
        if (curr.dataIndex === 'schoolCode') {
          return [...a, {
            title: curr.title,
            dataIndex: curr.dataIndex,
            width: curr.width || 80,
            render: (value: string, record: unknown, index: number) => {
              if (index === 0) {
                return {
                  children: value || '全市',
                  props: {
                    colSpan: c.length
                  }
                };
              } else {
                return value;
              }
            }
          }];
        } else {
          return [...a, {
            title: curr.title,
            dataIndex: curr.dataIndex,
            width: curr.width || 80
          }];
        }
      } else {
        return [...a, ...curr.children?.filter(i => i.active).map(i => ({
          title: curr.dataIndex === 'avgScore' ? i.title + '-' + curr.title : curr.title + i.title,
          width: 80,
          dataIndex: i.dataIndex
        })) as any];
      }
    }, [] as any);

    const dataSource = [
      { schoolCode: decodeURI(name) + '-' + courseRecord[courseDcode] + '-成绩分段分析' },
      c.reduce((a: any, curr: any) => {
        return {...a, [curr.dataIndex as string]: curr.title };
      }, {} as Record<string, string>),
      ...list.map(i => {
        return {
          ...i,
          areaCode: schoolAreaRecord[i.areaCode] || '全市'
        };
      })
    ] as unknown as any;
    exportFile({
      columns: c as unknown as any,
      dataSource: formatAnalysisTableRows(filterNull(dataSource), c) as unknown as any,
      showHeader: false,
      raw: true,
      onTxBodyRow: (_: unknown, index: number) => {
        if (index <= 1) {
          return {
            style: {
              borderStyle: 'thin',
              borderColorRgb: '000',
              alignmentHorizontal: 'center',
              fontBold: true,
              fontName: '宋体'
            }
          };
        }
        return {
          style: {
            borderStyle: 'thin',
            borderColorRgb: '000',
            alignmentHorizontal: 'center',
            fontName: '宋体'
          }
        };
      },
      fileName: decodeURI(name) + '-' + courseRecord[courseDcode] + '-成绩分段分析.xlsx'
    });
    addLogoPt(examid, decodeURI(name) + '-' + courseRecord[courseDcode] + '-成绩分段分析.xlsx');
    // exportFile();
  };

  return (
    <>
      <div className={style.title}>
        <div>成绩分段分析</div>
        <div>
          <span>导出:</span>
          {
            canExportStudentScore && <Button loading={exportStudentScoreLoading} onClick={downloadStudentScoreExcel}>学生成绩表</Button>
          }
          <Button onClick={exportSingleAnalysisExcel}>成绩分段分析</Button>
        </div>
      </div>
      <div className={style.table}>
        <Wrapper>
          <div className={style.title} style={{marginBottom: '20px'}}>
            <div className={style.searchPanel}>
              <span>分数段：</span>
              <Radio.Group value={split} onChange={e => splitChange(e.target.value)}>
                <Radio.Button value={5}>5</Radio.Button>
                <Radio.Button value={10}>10</Radio.Button>
                <Radio.Button value={15}>15</Radio.Button>
                <Radio.Button value={20}>20</Radio.Button>
              </Radio.Group>
              <span>分/段</span>
            </div>
            <ColumnSetting
              onColumnChange={onColumnChange}
              onReset={columnReset}
              showGradeLine={false}
              isSingle={true}
              storageKey={SCORE_SEGMENT_COLUMN_SETTING_STORAGE_KEY}
              storageVersion={SCORE_SEGMENT_COLUMN_SETTING_STORAGE_VERSION}
            />
          </div>
          <div className={style.tableWrapper}>
            <table className={style.uniTable}>
              <thead>
                <tr>
                  {
                    columns.filter(i => i.active).map((i, index) => (
                      <th
                        key={`${i.dataIndex}${index}`}
                        colSpan={(i.children && i.children.filter(o => o.active).length) || 1}
                        rowSpan={!i.children || i.children.filter(o => o.active && o.title !== '人数').length === 0 ? 2 : 1}
                        style={{width: `${i.width || (i.children?.length && i.children?.filter(o => o.active).length * 60 + 60) || 80}px`}}
                        className={`${(i.dataIndex === mouseAdr.y || i.children?.find(el => el.dataIndex === mouseAdr.y)) && style.current}`}
                      >
                        {i.title}
                      </th>
                    ))
                  }
                </tr>
                {
                  secondRow.filter(i => i.active && i.title !== '人数').length > 0 && (
                    <tr>
                      {
                        secondRow.filter(i => i.active).map((i, index) => (
                          <th
                            className={`${i.dataIndex === mouseAdr.y && style.current}`}
                            key={`${i.dataIndex}${index}`}
                            style={{width: `${i.width || 80}px`}}
                          >
                            {i.title}
                          </th>
                        ))
                      }
                    </tr>
                  )
                }
              </thead>
              <tbody>
                {
                  list.map((i, index) => (
                    <tr key={index} className={`${index === mouseAdr.x && style.current}`}>{
                      bodyColumns.map((c) => (
                        <td
                          className={`${c.dataIndex === mouseAdr.y && style.current}`}
                          key={`${c.dataIndex}${index}`}
                          style={{width: `${c.width || 80}px`}}
                          onMouseOver={() => setMouseAdr({x: index, y: c.dataIndex as string || ''})}
                        >
                          {
                            formatAnalysisTableValue(c.render ? c.render(1, i as SingleCourseAnalysisStat, index) : i[c.dataIndex as string], c) as unknown as any
                          }
                        </td>
                      ))
                    }</tr>
                  ))
                }
                {
                  list.length === 0 && <tr><td colSpan={columns.length}>暂无数据</td></tr>
                }
              </tbody>
            </table>
          </div>
        </Wrapper>
      </div>
    </>
  );
};

export default SingleCourse;
